import api from './api';
import axios from 'axios'; // Import axios
import { ensureTenantId } from '../utils/tenantUtils';

// Flag to enable mock data while backend is being implemented
const ENABLE_MOCK_DATA = false; // Set to true to use mock data while API endpoints are not working

// Configure the base URL for API requests
// This should be your backend server URL, not the frontend server
const API_BASE_URL = process.env.VITE_API_URL || 'http://localhost:5000/api';

// Persistent mock data store for a more realistic experience
const mockDataStore = {
  token: {
    _id: 'mock-token-id',
    name: 'SkillCoin',
    symbol: 'SKC',
    description: 'Community token for rewarding member contributions and participation',
    baseRate: 0.01,
    isExchangeable: true,
    isTransferable: true,
    maxSupply: 1000000,
    totalSupply: 50000,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    settings: {}
  },
  statistics: {
    totalIssued: 50000,
    totalSpent: 15000,
    activeUsers: 42,
    transactionStats: [
      { _id: 'earn', totalAmount: 50000, count: 1250 },
      { _id: 'spend', totalAmount: 15000, count: 320 },
      { _id: 'transfer_out', totalAmount: 2500, count: 45 },
      { _id: 'transfer_in', totalAmount: 2500, count: 45 }
    ],
    activityStats: [
      { _id: 'content_creation', totalAmount: 15000, count: 300 },
      { _id: 'knowledge_sharing', totalAmount: 10000, count: 200 },
      { _id: 'forum_activity', totalAmount: 12500, count: 500 },
      { _id: 'course_completion', totalAmount: 7500, count: 150 },
      { _id: 'event_participation', totalAmount: 5000, count: 100 }
    ],
    topEarners: [
      { userId: 'user1', memberName: 'John Doe', balance: 2500, totalEarned: 3000, totalSpent: 500 },
      { userId: 'user2', memberName: 'Jane Smith', balance: 2200, totalEarned: 2500, totalSpent: 300 },
      { userId: 'user3', memberName: 'Bob Johnson', balance: 1800, totalEarned: 2000, totalSpent: 200 }
    ],
    recentTransactions: [
      { type: 'EARN', amount: 50, description: 'Workshop attendance', date: new Date().toISOString() },
      { type: 'SPEND', amount: 20, description: 'Merchandise purchase', date: new Date().toISOString() },
      { type: 'TRANSFER', amount: 30, description: 'Transfer to Bob', date: new Date().toISOString() }
    ],
    monthlyStats: [
      { month: 'Jan', issued: 1000, spent: 500 },
      { month: 'Feb', issued: 1200, spent: 600 },
      { month: 'Mar', issued: 1500, spent: 750 }
    ]
  },
  earningActivities: [
    { 
      _id: 'act1', 
      name: 'Content Creation', 
      description: 'Earn tokens by creating valuable content for the community',
      tokenAmount: 50, 
      activityType: 'content_creation',
      isEnabled: true,
      category: 'content',
      frequency: 'daily',
      cooldownPeriod: 24,
      createdAt: new Date().toISOString()
    },
    { 
      _id: 'act2', 
      name: 'Workshop Attendance', 
      description: 'Earn tokens by attending community workshops and events',
      tokenAmount: 25, 
      activityType: 'event_participation',
      isEnabled: true,
      category: 'community',
      frequency: 'once',
      cooldownPeriod: 0,
      createdAt: new Date().toISOString()
    },
    { 
      _id: 'act3', 
      name: 'Forum Participation', 
      description: 'Earn tokens by actively participating in community discussions',
      tokenAmount: 10, 
      activityType: 'forum_activity',
      isEnabled: true,
      category: 'engagement',
      frequency: 'daily',
      cooldownPeriod: 24,
      createdAt: new Date().toISOString()
    }
  ],
  usageOptions: [
    { 
      _id: 'opt1',
      name: 'Community T-Shirt', 
      description: 'Redeem tokens for an exclusive community t-shirt',
      tokenCost: 100,
      optionType: 'physical_item',
      isActive: true,
      details: {
        limitPerMember: 2,
        durationDays: 0
      },
      createdAt: new Date().toISOString()
    },
    { 
      _id: 'opt2',
      name: 'Premium Workshop Access', 
      description: 'Get exclusive access to premium workshops and training',
      tokenCost: 200,
      optionType: 'content_access',
      isActive: true,
      details: {
        limitPerMember: 0,
        durationDays: 30
      },
      createdAt: new Date().toISOString()
    },
    { 
      _id: 'opt3',
      name: 'Mentorship Session', 
      description: 'One-on-one mentorship session with a community expert',
      tokenCost: 300,
      optionType: 'service',
      isActive: true,
      details: {
        limitPerMember: 1,
        durationDays: 0
      },
      createdAt: new Date().toISOString()
    }
  ],
  disbursements: [
    { 
      _id: 'dis1', 
      recipients: 'ALL_MEMBERS', 
      amount: 50, 
      reason: 'Monthly reward', 
      status: 'COMPLETED',
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days ago
    },
    { 
      _id: 'dis2', 
      recipients: 'SPECIFIC_MEMBERS', 
      memberIds: ['member1', 'member2'],
      amount: 100, 
      reason: 'Event participation', 
      status: 'COMPLETED',
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString() // 3 days ago
    }
  ],
  wallets: [
    { 
      _id: 'wallet1', 
      tokenId: 'mock-token-id', 
      balance: 500, 
      userId: 'current-user',
      transactions: [
        { _id: 'trx1', type: 'EARN', amount: 50, description: 'Workshop attendance', date: new Date().toISOString() },
        { _id: 'trx2', type: 'SPEND', amount: 20, description: 'Merchandise purchase', date: new Date().toISOString() }
      ]
    }
  ],
  marketplaceItems: [
    { 
      _id: 'item1', 
      title: 'Community T-Shirt', 
      description: 'Show your community pride with this exclusive t-shirt!', 
      price: 50, 
      quantity: 100,
      listingType: 'physical_item',
      status: 'active',
      categories: ['merchandise', 'clothing'],
      tags: ['exclusive', 'apparel'],
      images: [
        { url: 'https://via.placeholder.com/150?text=T-Shirt', alt: 'Community T-Shirt' }
      ],
      isGloballyAvailable: false,
      allowNegotiation: false,
      createdAt: new Date().toISOString()
    },
    { 
      _id: 'item2', 
      title: 'Premium Workshop Access', 
      description: 'Access to exclusive workshops and training materials', 
      price: 100, 
      quantity: 50,
      listingType: 'digital_content',
      status: 'active',
      categories: ['education', 'digital'],
      tags: ['workshop', 'learning'],
      images: [
        { url: 'https://via.placeholder.com/150?text=Workshop', alt: 'Premium Workshop' }
      ],
      isGloballyAvailable: true,
      allowNegotiation: false,
      createdAt: new Date().toISOString()
    },
    { 
      _id: 'item3', 
      title: 'Mentorship Session', 
      description: 'One-hour mentorship session with a community expert', 
      price: 150, 
      quantity: 20,
      listingType: 'service',
      status: 'active',
      categories: ['mentorship', 'service'],
      tags: ['one-on-one', 'expert'],
      images: [
        { url: 'https://via.placeholder.com/150?text=Mentorship', alt: 'Mentorship Session' }
      ],
      isGloballyAvailable: false,
      allowNegotiation: true,
      createdAt: new Date().toISOString()
    }
  ],
  purchases: [
    { 
      _id: 'purchase1', 
      itemId: 'item1', 
      userId: 'current-user', 
      status: 'COMPLETED',
      price: 50,
      createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() // 10 days ago
    },
    { 
      _id: 'purchase2', 
      itemId: 'item2', 
      userId: 'current-user', 
      status: 'COMPLETED',
      price: 100,
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() // 5 days ago
    }
  ]
};

// Simulate API response delay
const mockDelay = () => new Promise(resolve => setTimeout(resolve, 300));

// Add this function to ensure authorization headers are set properly
const configureAuthHeaders = () => {
  // Get auth token from localStorage or wherever your app stores it
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  
  // Return config with auth headers if token exists
  return token ? {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-auth-token': token
    }
  } : {};
};

// Check if the API endpoint is available
const checkApiAvailability = async (endpoint) => {
  try {
    if (!ENABLE_MOCK_DATA) {
      await api.options(endpoint);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Generate mock API response with success format
const mockResponse = (data) => ({
  success: true,
  data,
  message: 'Operation successful',
  timestamp: new Date().toISOString()
});

// Generate mock pagination for marketplace items
const mockPagination = (items, page = 1, limit = 10) => {
  const startIndex = (page - 1) * limit;
  const endIndex = page * limit;
  const paginatedItems = items.slice(startIndex, endIndex);
  
  return {
    items: paginatedItems,
    pagination: {
      currentPage: page,
      totalPages: Math.ceil(items.length / limit),
      totalItems: items.length,
      itemsPerPage: limit
    }
  };
};

/**
 * Service for managing tenant tokens (SkillCoin)
 */
const tenantTokenService = {
  /**
   * Get the token for the current tenant
   */
  getTenantToken: async () => {
    try {
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        console.log('Using mock token data');
        return mockResponse(mockDataStore.token);
      }
      
      // Ensure tenant ID is available
      ensureTenantId();
      
      console.log('Fetching token');
      
      const response = await api.get('/v1/skillcoin/tokens');
      
      console.log('Token API response:', response.status, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant token:', error);
      
      // Better error diagnostics
      if (error.response) {
        console.error('Server responded with:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      }
      
      // Fall back to mock data on error
      if (ENABLE_MOCK_DATA) {
        console.log('Falling back to mock data after API error');
        await mockDelay();
        return mockResponse(mockDataStore.token);
      }
      
      throw error;
    }
  },

  /**
   * Get token settings only
   */
  getTokenSettings: async () => {
    try {
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        return mockResponse(mockDataStore.token.settings || {});
      }
      
      // Ensure tenant ID is available
      ensureTenantId();
      
      const response = await api.get('/v1/skillcoin/tokens/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching token settings:', error);
      
      // Fall back to mock data on error
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        return mockResponse(mockDataStore.token.settings || {});
      }
      
      throw error;
    }
  },

  /**
   * Update token settings only
   */
  updateTokenSettings: async (settings) => {
    try {
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        mockDataStore.token.settings = {
          ...mockDataStore.token.settings,
          ...settings
        };
        return mockResponse(mockDataStore.token.settings);
      }
      
      const authConfig = configureAuthHeaders();
      const response = await api.put(
        `${API_BASE_URL}/v1/skillcoin/tokens/settings`, 
        { settings }, 
        authConfig
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating token settings:', error);
      
      // Fall back to mock data on error
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        mockDataStore.token.settings = {
          ...mockDataStore.token.settings,
          ...settings
        };
        return mockResponse(mockDataStore.token.settings);
      }
      
      throw error;
    }
  },

  /**
   * Create a new token for the tenant
   */
  createToken: async (tokenData) => {
    try {
      console.log('Creating token with data:', tokenData);
      
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        mockDataStore.token = {
          ...mockDataStore.token,
          ...tokenData,
          _id: 'new-token-id-' + Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        console.log('Created mock token:', mockDataStore.token);
        return mockResponse(mockDataStore.token);
      }
      
      // Make sure we explicitly set the tenant ID from localStorage if available
      const tenantId = localStorage.getItem('tenantId');
      const userId = localStorage.getItem('userId');
      const authConfig = configureAuthHeaders();
      
      // Add tenant ID to request headers if available
      if (tenantId) {
        if (!authConfig.headers) {
          authConfig.headers = {};
        }
        authConfig.headers['x-tenant-id'] = tenantId;
      }
      
      // Prepare the request data with additional information
      const requestData = {
        ...tokenData,
        tenantId: tenantId || undefined,
        createdBy: userId || undefined,
        earningActivities: tokenData.earningActivities || [],
        usageOptions: tokenData.usageOptions || [],
        isActive: tokenData.isActive !== undefined ? tokenData.isActive : true,
        totalSupply: tokenData.totalSupply || 0,
        maxSupply: tokenData.maxSupply || 1000000,
      };
      
      // Log the final request payload
      console.log('Sending API request with data:', requestData);
      
      const response = await api.post(
        `${API_BASE_URL}/v1/skillcoin/tokens`, 
        requestData, 
        authConfig
      );
      console.log('API response:', response.data);
      
      return response.data.success ? {
        success: true,
        data: response.data.data
      } : response.data;
    } catch (error) {
      console.error('Error creating token:', error);
      
      // Log the specific error details for debugging
      if (error.response) {
        console.error('Server responded with:', error.response.status, error.response.data);
      }
      
      // Fall back to mock data on error
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        mockDataStore.token = {
          ...mockDataStore.token,
          ...tokenData,
          _id: 'new-token-id-' + Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        return mockResponse(mockDataStore.token);
      }
      
      throw error;
    }
  },

  /**
   * Update the tenant's token settings
   */
  updateToken: async (tokenData) => {
    try {
      console.log('Updating token with data:', tokenData);
      
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        mockDataStore.token = {
          ...mockDataStore.token,
          ...tokenData,
          updatedAt: new Date().toISOString()
        };
        return mockResponse(mockDataStore.token);
      }
      
      // Make sure we explicitly set the tenant ID from localStorage if available
      const tenantId = localStorage.getItem('tenantId');
      const authConfig = configureAuthHeaders();
      
      // Add tenant ID to request headers if available
      if (tenantId) {
        if (!authConfig.headers) {
          authConfig.headers = {};
        }
        authConfig.headers['x-tenant-id'] = tenantId;
      }
      
      // Add tenant ID to the request body as a fallback
      const requestData = {
        ...tokenData,
        tenantId: tenantId || tokenData.tenantId || undefined
      };
      
      console.log('Sending token update API request with data:', requestData);
      
      const response = await api.put(
        `${API_BASE_URL}/v1/skillcoin/tokens`, 
        requestData, 
        authConfig
      );
      
      console.log('Token update API response:', response.data);
      
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Error updating token:', error);
      
      // Enhanced error logging
      if (error.response) {
        console.error('Server responded with:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      }
      
      // Fall back to mock data on error
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        mockDataStore.token = {
          ...mockDataStore.token,
          ...tokenData,
          updatedAt: new Date().toISOString()
        };
        return mockResponse(mockDataStore.token);
      }
      
      throw error;
    }
  },

  /**
   * Get token statistics
   */
  getTokenStatistics: async (period = 'month') => {
    try {
      const authConfig = configureAuthHeaders();
      const response = await api.get(
        `${API_BASE_URL}/v1/skillcoin/tokens/statistics?period=${period}`, 
        authConfig
      );
      return response.data.success ? response.data.data : null;
    } catch (error) {
      console.error('Error fetching token statistics:', error);
      
      // Fall back to mock data on error
      if (ENABLE_MOCK_DATA) {
        await mockDelay();
        console.log('Using mock token statistics data due to API error');
        return mockDataStore.statistics;
      }
      
      throw error;
    }
  }
};

/**
 * Service for token wallet operations
 */
const tokenWalletService = {
  getWallet: async (walletId, cacheBust = Date.now()) => {
    try {
      const response = await api.get(`/api/skillcoin/wallets/${walletId}?_t=${cacheBust}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      // Convert string values to numbers where appropriate
      const data = response.data;
      if (data) {
        if (typeof data.balance === 'string') data.balance = parseFloat(data.balance) || 0;
        if (typeof data.totalEarned === 'string') data.totalEarned = parseFloat(data.totalEarned) || 0;
        if (typeof data.totalSpent === 'string') data.totalSpent = parseFloat(data.totalSpent) || 0;
        if (typeof data.totalSent === 'string') data.totalSent = parseFloat(data.totalSent) || 0;
        
        // If metrics exist, convert those too
        if (data.metrics) {
          if (typeof data.metrics.totalEarned === 'string') data.metrics.totalEarned = parseFloat(data.metrics.totalEarned) || 0;
          if (typeof data.metrics.totalSpent === 'string') data.metrics.totalSpent = parseFloat(data.metrics.totalSpent) || 0;
          if (typeof data.metrics.totalSent === 'string') data.metrics.totalSent = parseFloat(data.metrics.totalSent) || 0;
          if (typeof data.metrics.availableBalance === 'string') data.metrics.availableBalance = parseFloat(data.metrics.availableBalance) || 0;
        }
      }
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching wallet:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch wallet data'
      };
    }
  },
  
  getTransactions: async (walletId, options = {}) => {
    try {
      const { limit = 500, offset = 0, type, startDate, endDate, cacheBust = Date.now() } = options;
      
      // Build query parameters
      let params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      params.append('_t', cacheBust);
      
      if (type) params.append('type', type);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/api/skillcoin/wallets/${walletId}/transactions?${params.toString()}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      // Convert string values to numbers where appropriate
      const data = response.data;
      if (Array.isArray(data)) {
        data.forEach(tx => {
          if (typeof tx.amount === 'string') tx.amount = parseFloat(tx.amount) || 0;
        });
      }
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch transaction data'
      };
    }
  },
};

/**
 * Service for token earning activities
 */
const tokenEarningService = {
  /**
   * Get all earning activities for a token
   */
  getEarningActivities: async () => {
    try {
      console.log('Fetching earning activities from tenantTokenService');
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure auth headers for fetch
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Try the primary endpoint first
      let response;
      try {
        console.log('Trying primary endpoint for activities');
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities`, { headers });
        
        if (!response.ok) {
          throw new Error(`Primary endpoint failed: ${response.status}`);
        }
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback endpoint', primaryError);
        // Try the fallback endpoint
        try {
          response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/earning-activities`, { headers });
          
          if (!response.ok) {
            throw new Error(`Fallback endpoint failed: ${response.status}`);
          }
        } catch (fallbackError) {
          console.warn('Fallback endpoint also failed', fallbackError);
          // Return mock/empty data instead of throwing
          if (ENABLE_MOCK_DATA) {
            console.log('Using mock data after API failures');
            return mockDataStore.earningActivities;
          }
          // Return empty array as a last resort to prevent UI breakage
          return [];
        }
      }
      
      const data = await response.json();
      console.log('Activities response:', data);
      
      // Handle different response formats
      if (data.success && Array.isArray(data.data)) {
        return data.data;
      } else if (Array.isArray(data)) {
        return data;
      } else if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      
      // If response format is invalid but we got here, return empty array
      console.warn('Invalid response format from API, returning empty array');
      return [];
    } catch (error) {
      console.error('Error fetching earning activities in tenantTokenService:', error);
      // Return empty array instead of throwing to prevent UI breakage
      if (ENABLE_MOCK_DATA) {
        console.log('Using mock data after error');
        return mockDataStore.earningActivities;
      }
      return [];
    }
  },

  /**
   * Create a new earning activity
   */
  createEarningActivity: async (activityData) => {
    try {
      console.log('Creating earning activity with data:', activityData);
      
      // Validate required fields
      if (!activityData.activityType || !activityData.name || !activityData.tokenAmount) {
        throw new Error('Activity type, name, and token amount are required');
      }
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure auth headers
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId,
        'Content-Type': 'application/json'
      };
      
      // Structure request data correctly for the API
      const requestData = {
        name: activityData.name,
        description: activityData.description || '',
        tokenAmount: parseFloat(activityData.tokenAmount),
        activityType: activityData.activityType,
        category: activityData.category || 'Other',
        frequency: activityData.frequency || 'once',
        cooldownPeriod: parseInt(activityData.cooldownPeriod || 0),
        maxDaily: parseInt(activityData.maxDaily || 0),
        isEnabled: activityData.isActive !== undefined ? activityData.isActive : true,
        tenantId
      };
      
      console.log('Sending activity data to API:', requestData);
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Try the primary endpoint first
      let response;
      let responseData;
      try {
        console.log('Trying primary endpoint for activity creation');
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Primary endpoint error:', errorText);
          throw new Error(`Primary endpoint failed: ${response.status}`);
        }
        
        responseData = await response.json();
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback endpoint', primaryError);
        
        // Try the fallback endpoint
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/earning-activities`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Fallback endpoint error:', errorText);
          throw new Error(`API error: ${response.status}`);
        }
        
        responseData = await response.json();
      }
      
      console.log('Activity creation response:', responseData);
      
      // Return the newly created activity
      if (responseData.success && responseData.data) {
        return responseData.data;
      } else if (responseData.data) {
        return responseData.data;
      } else {
        return responseData;
      }
    } catch (error) {
      console.error('Error creating earning activity:', error);
      throw error;
    }
  },

  /**
   * Update an existing earning activity
   * @param {string} activityId - ID of the activity to update
   * @param {object} activityData - Updated activity data
   */
  updateEarningActivity: async (activityId, activityData) => {
    try {
      console.log(`Updating earning activity ${activityId} with data:`, activityData);
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure request headers
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId
      };
      
      // Structure the request data
      const requestData = {
        name: activityData.name,
        description: activityData.description || '',
        tokenAmount: parseFloat(activityData.tokenAmount),
        activityType: activityData.activityType,
        isActive: activityData.isActive !== undefined ? activityData.isActive : true,
        category: activityData.category || 'Other',
        frequency: activityData.frequency || 'once',
        maxDaily: parseInt(activityData.maxDaily || 0)
      };
      
      console.log('Sending activity update to API:', requestData);
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Try primary endpoint first
      let response;
      let responseData;
      try {
        console.log(`Trying primary endpoint for updating activity ${activityId}`);
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities/${activityId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Primary endpoint error:', errorText);
          throw new Error(`Primary endpoint failed: ${response.status}`);
        }
        
        responseData = await response.json();
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback endpoint', primaryError);
        
        // Try the fallback endpoint
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/earning-activities/${activityId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Fallback endpoint error:', errorText);
          throw new Error(`API error: ${response.status}`);
        }
        
        responseData = await response.json();
      }
      
      console.log('Activity update response:', responseData);
      
      // Return the updated activity
      if (responseData.success && responseData.data) {
        return responseData.data;
      } else if (responseData.data) {
        return responseData.data;
      } else {
        return responseData;
      }
    } catch (error) {
      console.error('Error updating earning activity:', error);
      throw error;
    }
  },

  /**
   * Delete an earning activity
   * @param {string} activityId - The ID of the activity to delete
   * @returns {Promise<Object>} The result of the delete operation
   */
  deleteEarningActivity: async (activityId) => {
    try {
      console.log(`Deleting earning activity ${activityId}`);
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure headers
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Try primary endpoint first
      let response;
      let responseData;
      try {
        console.log(`Trying primary endpoint for deleting activity ${activityId}`);
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities/${activityId}`, {
          method: 'DELETE',
          headers
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Server error: ${response.status}`);
        }
        
        responseData = await response.json();
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback endpoint', primaryError);
        
        // Try the fallback endpoint
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/earning-activities/${activityId}`, {
          method: 'DELETE',
          headers
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Server error: ${response.status}`);
        }
        
        responseData = await response.json();
      }
      
      console.log('Activity deletion response:', responseData);
      
      // Return success status
      return responseData;
    } catch (error) {
      console.error('Error deleting earning activity:', error);
      throw error;
    }
  },

  /**
   * Submit evidence for an activity with file attachments
   * @param {Object} formData - The submission data
   * @param {Array} files - Array of files to upload as evidence
   * @returns {Promise<Object>} The created submission
   */
  submitEvidenceWithFiles: async (formData, files) => {
    try {
      console.log('Submitting evidence with files:', { formData, fileCount: files?.length || 0 });
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Create form data for multipart submission
      const multipartFormData = new FormData();
      
      // Add the form fields
      multipartFormData.append('activityId', formData.activityId);
      multipartFormData.append('evidence', formData.evidence);
      multipartFormData.append('notes', formData.notes || '');
      multipartFormData.append('userId', formData.userId || localStorage.getItem('userId'));
      multipartFormData.append('tenantId', tenantId);
      
      // Add the files
      if (files && files.length > 0) {
        files.forEach((file) => {
          multipartFormData.append('evidenceFiles', file);
        });
      }
      
      // Configure headers for multipart/form-data
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId
        // Note: Don't set Content-Type for multipart/form-data as the browser needs to set the boundary
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Try the primary endpoint first
      let response;
      let responseData;
      
      try {
        console.log('Trying primary endpoint for submission');
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities/submissions`, {
          method: 'POST',
          headers,
          body: multipartFormData
        });
        
        if (!response.ok) {
          throw new Error(`Primary endpoint failed: ${response.status}`);
        }
        
        responseData = await response.json();
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback endpoint', primaryError);
        
        // Try the fallback endpoint
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/submissions`, {
          method: 'POST',
          headers,
          body: multipartFormData
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Fallback endpoint error:', errorText);
          throw new Error(`API error: ${response.status}`);
        }
        
        responseData = await response.json();
      }
      
      console.log('Submission response:', responseData);
      
      return responseData;
    } catch (error) {
      console.error('Error submitting evidence with files:', error);
      throw error;
    }
  },

  /**
   * Get submission history for the current user
   * @returns {Promise<Array>} The submission history
   */
  getSubmissionHistory: async () => {
    try {
      console.log('Fetching submission history');
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure auth headers for fetch
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Try the primary endpoint first
      let response;
      try {
        console.log('Trying primary endpoint for submission history');
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities/submissions`, {
          headers
        });
        
        if (!response.ok) {
          throw new Error(`Primary endpoint failed: ${response.status}`);
        }
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback endpoint', primaryError);
        
        // Try the fallback endpoint
        response = await fetch(`${apiBaseUrl}/v1/skillcoin/submissions`, {
          headers
        });
        
        if (!response.ok) {
          throw new Error(`Fallback endpoint failed: ${response.status}`);
        }
      }
      
      const data = await response.json();
      console.log('Submission history response:', data);
      
      if (data.success && data.data) {
        return data;
      } else if (Array.isArray(data)) {
        return { success: true, data };
      } else if (data.data && Array.isArray(data.data)) {
        return data;
      }
      
      throw new Error('Invalid response format from API');
    } catch (error) {
      console.error('Error fetching submission history:', error);
      throw error;
    }
  }
};

/**
 * Service for token distributions and disbursements
 */
const tokenDistributionService = {
  /**
   * Get all token distributions/disbursements
   * @returns {Promise<Array>} List of token distributions
   */
  getDistributions: async () => {
    try {
      console.log('Fetching token distributions');
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure auth headers for fetch
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/disbursements`, { headers });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch distributions: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Distributions response:', data);
      
      if (data.success && Array.isArray(data.data)) {
        return data.data;
      } else if (Array.isArray(data)) {
        return data;
      } else if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      
      throw new Error('Invalid response format from API');
    } catch (error) {
      console.error('Error fetching token distributions:', error);
      throw error;
    }
  },
  
  /**
   * Create a new token disbursement
   * @param {Object} disbursementData - The data for the new disbursement
   * @returns {Promise<Object>} The created disbursement data
   */
  createDisbursement: async (disbursementData) => {
    try {
      console.log('Creating token disbursement:', disbursementData);
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure headers
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/disbursements`, {
        method: 'POST',
        headers,
        body: JSON.stringify(disbursementData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Failed to create disbursement: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Disbursement creation response:', data);
      
      if (data.success && data.data) {
        return data.data;
      }
      
      return data;
    } catch (error) {
      console.error('Error creating token disbursement:', error);
      throw error;
    }
  },
  
  /**
   * Get members eligible for token disbursement
   * @returns {Promise<Array>} List of eligible members
   */
  getEligibleMembers: async () => {
    try {
      console.log('Fetching eligible members for disbursement');
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure auth headers for fetch
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${apiBaseUrl}/v1/members?active=true`, { headers });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch eligible members: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Eligible members response:', data);
      
      // Handle different response formats
      if (data.success && Array.isArray(data.data)) {
        return data.data;
      } else if (Array.isArray(data)) {
        return data;
      } else if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      
      throw new Error('Invalid response format from API');
    } catch (error) {
      console.error('Error fetching eligible members:', error);
      throw error;
    }
  },
  
  /**
   * Create a bulk token disbursement to multiple members
   * @param {Object} bulkDisbursementData - The data for the bulk disbursement
   * @returns {Promise<Object>} The result of the bulk operation
   */
  createBulkDisbursement: async (bulkDisbursementData) => {
    try {
      console.log('Creating bulk token disbursement:', bulkDisbursementData);
      
      // Get tenant ID and auth token
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }
      
      // Configure headers
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId
      };
      
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${apiBaseUrl}/v1/skillcoin/tokens/disbursements/bulk`, {
        method: 'POST',
        headers,
        body: JSON.stringify(bulkDisbursementData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Failed to create bulk disbursement: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Bulk disbursement creation response:', data);
      
      if (data.success && data.data) {
        return data.data;
      }
      
      return data;
    } catch (error) {
      console.error('Error creating bulk token disbursement:', error);
      throw error;
    }
  }
};

/**
 * Service for marketplace operations
 */
const marketplaceService = {
  /**
   * Get all marketplace items
   */
  getItems: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch marketplace items');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching marketplace items:', error);
      throw error;
    }
  },

  /**
   * Get a specific marketplace item
   */
  getItem: async (itemId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/${itemId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch marketplace item');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching marketplace item:', error);
      throw error;
    }
  },

  /**
   * Create a new marketplace item
   */
  createItem: async (itemData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        },
        body: JSON.stringify(itemData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create marketplace item');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating marketplace item:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing marketplace item
   */
  updateItem: async (itemId, itemData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        },
        body: JSON.stringify(itemData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update marketplace item');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating marketplace item:', error);
      throw error;
    }
  },
  
  /**
   * Delete a marketplace item
   */
  deleteItem: async (itemId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete marketplace item');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting marketplace item:', error);
      throw error;
    }
  },
  
  /**
   * Purchase a marketplace item
   */
  purchaseItem: async (itemId, quantity = 1) => {
    try {
      console.log(`Purchasing item ${itemId}, quantity: ${quantity}`);
      
      // Get authentication tokens
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId');
      
      if (!authToken || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }
      
      // Validate inputs before making the request
      if (!itemId) {
        throw new Error('Item ID is required');
      }
      
      if (!Number.isInteger(quantity) || quantity <= 0) {
        throw new Error('Quantity must be a positive integer');
      }
      
      // Make the purchase request with quantity in the request body
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/${itemId}/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'x-tenant-id': tenantId
        },
        body: JSON.stringify({ quantity })
      });
      
      // If response is not OK, try to get detailed error information
      if (!response.ok) {
        let errorMessage = `Failed to purchase item: ${response.status}`;
        
        try {
          const errorData = await response.json();
          console.error('API error response:', errorData);
          
          if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
        }
        
        // Handle specific error codes
        if (response.status === 400) {
          throw new Error(errorMessage || 'Invalid purchase request. Please check item availability and your token balance.');
        } else if (response.status === 404) {
          throw new Error('Item not found or no longer available.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to purchase this item.');
        } else if (response.status === 401) {
          throw new Error('Your session has expired. Please log in again.');
        } else {
          throw new Error(errorMessage);
        }
      }
      
      // Parse and return successful response
      const data = await response.json();
      console.log('Purchase successful:', data);
      return data;
    } catch (error) {
      console.error('Error purchasing marketplace item:', error);
      throw error;
    }
  },
  
  /**
   * Get purchase history
   */
  getPurchaseHistory: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/purchases`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch purchase history');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      throw error;
    }
  },
  
  /**
   * Get categories and tags for marketplace
   */
  getCategories: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch marketplace categories');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching marketplace categories:', error);
      throw error;
    }
  },
  
  /**
   * Get listings by a specific provider
   */
  getListingsByProvider: async (providerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/provider/${providerId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch provider listings');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching provider listings:', error);
      throw error;
    }
  },
  
  /**
   * Get my listings (current member)
   */
  getMyListings: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/skillcoin/marketplace/my-listings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch my listings');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching my listings:', error);
      throw error;
    }
  },

  /**
   * Backward compatibility functions
   */
  createListing: async (itemData) => {
    return marketplaceService.createItem(itemData);
  },
  
  getListings: async () => {
    return marketplaceService.getItems();
  },
  
  getListing: async (itemId) => {
    return marketplaceService.getItem(itemId);
  },
  
  updateListing: async (itemId, itemData) => {
    return marketplaceService.updateItem(itemId, itemData);
  },
  
  deleteListing: async (itemId) => {
    return marketplaceService.deleteItem(itemId);
  }
};

// Define tokenMarketplaceService as a variable referring to marketplaceService
const tokenMarketplaceService = marketplaceService;

// Export all services
export { tenantTokenService, tokenWalletService, tokenEarningService, tokenDistributionService, tokenMarketplaceService, marketplaceService };